import { Box } from '@mui/material';
import {
  PracticalContentModel,
  PracticalProject,
} from '@protos/content_management/content.db_pb';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { pxToRem } from '../../../../commonUtils/resizeUtils';
import { IStyles } from '../../../../commonUtils/styleUtils';
import {
  GeneoIotError,
  GeneoIotFrame,
  GeneoIotHandle,
  GeneoIotProjectFile,
  GeneoIotProjectMeta,
  GeneoIotTelemetryEvent,
  GeneoIotTelemetryOptions,
  practicalProjectToProjectFile,
} from '../../GeneoIotFrame';

/** Space left under the simulator for the page's fixed pagination bar. */
const SIMULATOR_BOTTOM_GUTTER = 90;
/** Floor for the simulator height so it stays usable on very short viewports. */
const SIMULATOR_MIN_HEIGHT = 320;

const styles: IStyles = {
  simulatorWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  resourceMenuBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: pxToRem(6),
  },
  simulatorFrame: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    border: 'none',
    display: 'block',
  },
};

interface IProps {
  /** The authored reference project, straight off the resource's content. */
  practical: PracticalContentModel;
  /**
   * The teacher's own worked solution for this project, when they saved one.
   * Offered alongside the authored project rather than replacing it - the two
   * are different takes on the same activity.
   */
  teacherSolution?: PracticalProject;
  title?: string;
  /** Rendered in a bar above the simulator, e.g. the project's resource list. */
  resourceMenu?: React.ReactNode;
  /**
   * The student's - or their group's - last save. What the editor opens on; an
   * editable session.
   */
  savedProject?: GeneoIotProjectFile;
  meta?: GeneoIotProjectMeta;
  readOnly?: boolean;
  seedFromAuthoredProject?: boolean;
  /** Handle for pulling the project out on demand, e.g. before navigating away. */
  frameRef?: React.Ref<GeneoIotHandle>;
  onProject?: (project: GeneoIotProjectFile) => void;
  onDirty?: (dirty: boolean) => void;
  onError?: (error: GeneoIotError) => void;
  /** The embedded editor finished loading and is usable. */
  onReady?: () => void;
  /** Opt in to usage telemetry from the editor. Off unless set. */
  telemetry?: GeneoIotTelemetryOptions;
  onTelemetry?: (events: GeneoIotTelemetryEvent[]) => void;
}

export const PracticalElement = (props: IProps) => {
  const {
    practical,
    teacherSolution,
    title,
    resourceMenu,
    savedProject,
    meta,
    readOnly,
    seedFromAuthoredProject,
    frameRef,
    onProject,
    onDirty,
    onError,
    onReady,
    telemetry,
    onTelemetry,
  } = props;

  const simulatorRef = useRef<HTMLDivElement>(null);
  const [simulatorHeight, setSimulatorHeight] = useState(SIMULATOR_MIN_HEIGHT);
  const isReadOnly = readOnly ?? true;


  //~ A read-only view with nothing saved opens on a reference solution: the
  //~ teacher's own for this project when there is one, else the authored one.
  const referenceProject = teacherSolution ?? practical.project;
  const initialProject =
    savedProject ??
    ((seedFromAuthoredProject ?? true) && isReadOnly && referenceProject
      ? practicalProjectToProjectFile(referenceProject, meta)
      : undefined);

  /**
   * The samples the frame offers, in menu order: the content team's authored
   * project first, then the teacher's own solution for this project when there
   * is one. Each is named for the menu, because both files carry whatever name
   * they were authored under and two entries called the same thing tell the
   * student nothing.
   */
  const sampleProjects = useMemo(() => {
    const samples: { label: string; project?: PracticalProject }[] = [
      { label: 'Sample Project', project: practical.project },
      { label: "Teacher's Solution", project: teacherSolution },
    ];
    return samples
      .filter((sample) => !!sample.project)
      .map((sample) =>
        practicalProjectToProjectFile(sample.project as PracticalProject, {
          name: sample.label,
          author: sample.project?.meta?.author || '',
          description: sample.project?.meta?.description || '',
        })
      );
  }, [practical.project, teacherSolution]);

  useEffect(() => {
    const updateHeight = () => {
      const wrapper = simulatorRef.current;
      if (!wrapper) {
        return;
      }
      const topOffset = wrapper.getBoundingClientRect().top + window.scrollY;
      setSimulatorHeight(
        Math.max(
          window.innerHeight - topOffset - SIMULATOR_BOTTOM_GUTTER,
          SIMULATOR_MIN_HEIGHT
        )
      );
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);
    const observer = new ResizeObserver(updateHeight);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      ref={simulatorRef}
      sx={{ ...styles.simulatorWrapper, height: `${simulatorHeight}px` }}
    >
      {resourceMenu && <Box sx={styles.resourceMenuBar}>{resourceMenu}</Box>}
      <GeneoIotFrame
        ref={frameRef}
        initialProject={initialProject}
        meta={meta}
        sampleProjects={sampleProjects}
        readOnly={isReadOnly}
        onProject={onProject}
        onDirty={onDirty}
        onError={onError}
        onReady={onReady}
        telemetry={telemetry}
        onTelemetry={onTelemetry}
        sx={styles.simulatorFrame}
        title={title || 'Simulator'}
      />
    </Box>
  );
};
